import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { courseTemplates, CourseTemplate } from '../data/courseTemplates';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

const CourseTemplates: React.FC = () => {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState<CourseTemplate | null>(null);
  const [creating, setCreating] = useState(false);

  const handleSelectTemplate = (template: CourseTemplate) => {
    setSelectedTemplate(template);
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate) return;

    setCreating(true);
    try {
      // Create course from template
      const courseData = {
        courseOutline: {
          title: selectedTemplate.title,
          description: selectedTemplate.description,
          niche: selectedTemplate.niche,
          targetAudience: selectedTemplate.targetAudience,
          learningObjectives: [],
          modules: selectedTemplate.modules
        }
      };

      await axios.post(
        `${API_URL}/ai/save-course`,
        courseData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Course created from template!');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error creating course from template:', error);
      toast.error(error.response?.data?.error || 'Failed to create course');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back to Dashboard
              </button>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                  The Course Creator's Vault
                </h1>
                <p className="text-[10px] sm:text-xs text-gray-500">by Templimize AI</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {user?.name}
              </span>
              <button
                onClick={logout}
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            📚 Course Templates
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Start fast with pre-built course structures for the most popular niches.
            Simply select a template, customize it, and launch!
          </p>
        </div>

        {/* Templates Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {courseTemplates.map((template) => (
            <div
              key={template.id}
              onClick={() => handleSelectTemplate(template)}
              className={`bg-white rounded-2xl shadow-lg border-2 cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 ${
                selectedTemplate?.id === template.id
                  ? 'border-purple-600 ring-4 ring-purple-200'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="p-8">
                {/* Template Icon */}
                <div className="text-6xl mb-4 text-center">
                  {template.thumbnail}
                </div>

                {/* Template Title */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">
                  {template.title}
                </h3>

                {/* Template Meta */}
                <div className="flex flex-wrap justify-center gap-2 mb-4">
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-semibold rounded-full">
                    {template.niche}
                  </span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                    {template.difficulty}
                  </span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
                    {template.estimatedDuration}
                  </span>
                </div>

                {/* Template Description */}
                <p className="text-gray-600 mb-4 text-center line-clamp-3">
                  {template.description}
                </p>

                {/* Template Stats */}
                <div className="flex justify-around py-3 border-t border-gray-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {template.modules.length}
                    </div>
                    <div className="text-xs text-gray-500">Modules</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600">
                      {template.modules.reduce((sum, m) => sum + m.lessons.length, 0)}
                    </div>
                    <div className="text-xs text-gray-500">Lessons</div>
                  </div>
                </div>

                {/* Target Audience */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">TARGET AUDIENCE:</p>
                  <p className="text-sm text-gray-700 font-medium">{template.targetAudience}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Selected Template Detail & Action */}
        {selectedTemplate && (
          <div className="bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-2xl shadow-2xl p-8 mb-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="text-5xl">{selectedTemplate.thumbnail}</span>
                {selectedTemplate.title}
              </h2>

              <p className="text-purple-100 text-lg mb-6">
                {selectedTemplate.description}
              </p>

              <div className="bg-white bg-opacity-20 rounded-xl p-6 mb-6">
                <h3 className="text-xl font-bold mb-4">📋 Course Outline</h3>
                <div className="space-y-4">
                  {selectedTemplate.modules.map((module, idx) => (
                    <div key={idx} className="bg-white bg-opacity-10 rounded-lg p-4">
                      <h4 className="font-bold text-lg mb-2">
                        Module {idx + 1}: {module.title}
                      </h4>
                      <p className="text-purple-100 text-sm mb-3">{module.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {module.lessons.map((lesson, lessonIdx) => (
                          <span
                            key={lessonIdx}
                            className="px-2 py-1 bg-white bg-opacity-20 rounded text-xs"
                          >
                            {lesson.title} ({lesson.duration}min)
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleCreateFromTemplate}
                  disabled={creating}
                  className="bg-white text-purple-600 px-12 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? 'Creating Course...' : '🚀 Use This Template'}
                </button>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:bg-opacity-20 transition"
                >
                  Choose Different Template
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CourseTemplates;
